import { escapeHtml } from '../activities/scene';

/**
 * A yes/no question asked on the panel itself, in place of `window.confirm`.
 *
 * The browser's own dialog is the wrong tool on an interactive flat panel: it
 * is small, it sits at the top edge of an 86-inch screen where it is hard to
 * reach, and on some panels it expects Enter or Escape - there is no keyboard.
 * This one is big, centred, answered by touch, and closes on a tap outside.
 * The safe answer is the default and sits where the hand lands first.
 */

export interface SheetOptions {
  title: string;
  body: string;
  confirm: string;
  cancel?: string;
  /** The confirm button is painted as a warning. */
  destructive?: boolean;
}

export function askOnPanel(host: HTMLElement, options: SheetOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const root = document.createElement('div');
    root.className = 'sheet';
    root.innerHTML = `
      <div class="sheet__card" role="alertdialog" aria-modal="true" aria-labelledby="sheet-title" aria-describedby="sheet-body">
        <h2 id="sheet-title">${escapeHtml(options.title)}</h2>
        <p id="sheet-body">${escapeHtml(options.body)}</p>
        <div class="sheet__actions">
          <button type="button" class="sheet__cancel" data-answer="no">${escapeHtml(options.cancel ?? 'Batal')}</button>
          <button type="button" class="sheet__confirm${options.destructive ? ' sheet__confirm--danger' : ''}" data-answer="yes">${escapeHtml(options.confirm)}</button>
        </div>
      </div>`;

    const close = (answer: boolean): void => {
      root.remove();
      resolve(answer);
    };

    root.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (target === root) return close(false);
      const button = target.closest<HTMLElement>('[data-answer]');
      if (button) close(button.dataset['answer'] === 'yes');
    });

    host.append(root);
    root.querySelector<HTMLElement>('.sheet__cancel')?.focus();
  });
}

/* --- Full screen ---------------------------------------------------------------
   Installed, the app opens full screen by itself (the manifest asks for it).
   Opened in the panel's browser, it does not, and F11 needs a keyboard the
   panel does not have. So Mode Guru offers a button, only when it would help. */

export function canGoFullscreen(): boolean {
  if (!document.fullscreenEnabled || document.fullscreenElement) return false;
  return !window.matchMedia('(display-mode: fullscreen), (display-mode: standalone)').matches;
}

export function goFullscreen(): void {
  void document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => undefined);
}
